import { Service, service, inject } from './di'
import { LocationService } from './LocationService'
import { SeoDataService } from './SeoDataService'
import { SeoService } from './SeoService'
import { StorageService } from './StorageService'
import { DateTimeService } from './DateTimeService'

@service
export default class MetadataService extends Service {
    @inject locationService: LocationService
    @inject seoDataService: SeoDataService
    @inject seoService: SeoService
    @inject storage: StorageService
    @inject dateTimeService: DateTimeService

    reqParamsQuery: { [key: string]: string } = {}

    setReqParamsQuery(params: { [key: string]: string }) {
        this.reqParamsQuery = params
    }

    async setAmpLink(uri: string, validate: boolean = false) {
        const queryParams = new URLSearchParams(location.search)
        if (
            validate &&
            (!this.isCanonical() || queryParams.has('postal_code'))
        ) {
            return
        }
        const ampLink = document.querySelector("link[rel='amphtml']")
        const url = `https://flipp.com/amp${uri}`
        if (ampLink) {
            ampLink.setAttribute('href', url)
        } else {
            const link = document.createElement('link')
            link.setAttribute('rel', 'amphtml')
            link.setAttribute('href', url)
            document.head.appendChild(link)
        }
    }

    async setCanonicalAsync(hrefPromise: Promise<string>) {
        const href = await hrefPromise
        return this.setCanonicalLink(href)
    }

    async redirectAsync(
        hrefPromise: Promise<string>,
        redirectMessageKey: string
    ) {
        this.storage.setItem('message_key', redirectMessageKey)
        this.storage.setItem(
            'redirected_modal_closed',
            JSON.stringify({ closed: false })
        )
        const href = await hrefPromise
        return window.location.replace(href)
    }

    async setListingPageLink() {
        if (this.isCanonical()) {
            this.setCanonicalLink(this.getFlyersLink())
        }
    }

    async setCouponsPageLink() {
        if (this.isCanonical()) {
            this.setCanonicalLink(this.getCouponsLink())
        }
    }

    async setFlyerPageLink(slug: string) {
        if (this.isCanonical()) {
            this.setCanonicalLink(this.getSingleFlyerLink(slug))
        }
    }

    async setCouponPageLink(slug: string) {
        if (this.isCanonical()) {
            this.setCanonicalLink(this.getSingleCouponLink(slug))
        }
    }

    async setItemPageLink(slug: string) {
        if (this.isCanonical()) {
            this.setCanonicalLink(this.getItemLink(slug))
        }
    }

    getFlyersLink(): string {
        const inCity = this.getInCityParameter()
        if (inCity) {
            const locale = this.getCityBasedLocale(inCity)
            return `/${locale}/${inCity}/${this.getLocaleBasedFlyerNames(locale)}s`
        }

        const locale = this.getLocale()
        return `/${locale}/${this.locationService.getInCity()}/${this.getLocaleBasedFlyerNames(locale)}s`
    }

    getCouponsLink(): string {
        const inCity = this.getInCityParameter()
        if (inCity) {
            const locale = this.getCityBasedLocale(inCity)
            return `/${locale}/${inCity}/coupons`
        }

        const locale = this.getLocale()
        return `/${locale}/${this.locationService.getInCity()}/coupons`
    }

    getSingleFlyerLink(slug: string): string {
        const inCity = this.getInCityParameter()
        if (inCity) {
            const locale = this.getCityBasedLocale(inCity)
            return `/${locale}/${inCity}/${this.getLocaleBasedFlyerNames(locale)}/${slug}`
        }

        const locale = this.getLocale()
        return `/${locale}/${this.locationService.getInCity()}/${this.getLocaleBasedFlyerNames(locale)}/${slug}`
    }

    getSingleCouponLink(slug: string): string {
        const queryParams = new URLSearchParams(window.location.search)
        if (
            queryParams.has('in-city') &&
            queryParams.has('locale') &&
            this.seoService.isLocale(queryParams.get('locale'))
        ) {
            return this.getSelfReferenceLink()
        }

        const inCity = this.getInCityParameter()
        if (inCity) {
            const locale = this.getCityBasedLocale(inCity)
            return `/${locale}/${inCity}/coupon/${slug}`
        }

        const locale = this.getLocale()
        return `/${locale}/${this.locationService.getInCity()}/coupon/${slug}`
    }

    getItemLink(slug: string): string {
        const inCity = this.getInCityParameter()
        if (inCity) {
            const locale = this.getCityBasedLocale(inCity)
            return `/${locale}/${inCity}/item/${slug}`
        }

        const locale = this.getLocale()
        return `/${locale}/${this.locationService.getInCity()}/item/${slug}`
    }

    getCityBasedLocale(inCity: string): string {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        const [_, state] = inCity.split('-')
        /* eslint-enable @typescript-eslint/no-unused-vars */
        const isFrenchLocale =
            state === 'qc' && window.location.href.includes('fr')
        return this.seoDataService.isCanadianState(state)
            ? isFrenchLocale
                ? 'fr-ca'
                : 'en-ca'
            : 'en-us'
    }

    setCanonicalLink(href: string) {
        const canonical = document.querySelector("link[rel='canonical']")
        href = `https://flipp.com${href}`.toLowerCase()
        if (canonical) {
            canonical.setAttribute('href', href)
        } else {
            const link = document.createElement('link')
            link.setAttribute('rel', 'canonical')
            link.setAttribute('href', href)
            document.head.appendChild(link)
        }
    }

    removeCanonicalLink() {
        const canonical = document.querySelector("link[rel='canonical']")
        if (canonical) {
            canonical.parentNode.removeChild(canonical)
        }
    }

    getCanonicalUrl(inCity: string): string {
        if (this.isCanonicalLegacyUrl(inCity)) {
            return this.formatCanonicalLegacyUrl(inCity)
        }

        const pathname = window.location.pathname
        const path =
            pathname.indexOf(this.locationService.locale) === -1
                ? `/${this.getLocale()}/${pathname.split('/').slice(2).join('/')}`
                : pathname
        return `${path}`
    }

    getSelfReferenceLink(): string {
        return window.location.pathname + window.location.search
    }

    formatCanonicalLegacyUrl(inCity: string): string {
        const locale = this.getCityBasedLocale(inCity)
        const splitedPathname = window.location.pathname.split('/').slice(3)
        const path =
            splitedPathname.length > 1
                ? splitedPathname.join('/')
                : splitedPathname.length === 1
                  ? `/${splitedPathname.toString()}`
                  : ''
        return path.includes(inCity)
            ? `/${locale}/${inCity}`
            : `/${locale}/${inCity}${path}`
    }

    isCanonicalLegacyUrl(inCity: string): boolean {
        return Boolean(inCity)
    }

    removeRobotsNoIndexTag() {
        const robotsMetaTag = document.querySelector("meta[name='robots']")
        if (robotsMetaTag) {
            robotsMetaTag.parentNode.removeChild(robotsMetaTag)
        }
    }

    setRobotsNoIndexTag() {
        const robotsMetaTag = document.querySelector("meta[name='robots']")
        if (robotsMetaTag) {
            robotsMetaTag.setAttribute('content', 'noindex')
        } else {
            const meta = document.createElement('meta')
            meta.setAttribute('name', 'robots')
            meta.setAttribute('content', 'noindex')
            document.head.appendChild(meta)
        }
    }

    setUnavailableAfterTag(date: any) {
        const d = new Date(date)
        const myDatetimeString = this.dateTimeService.formatDate(
            d,
            'dd-LLL-yyyy hh:mm:ss'
        )
        const contentValue = `unavailable_after: ${myDatetimeString} EST`

        const robotsMetaTag = document.querySelector("meta[name='robots']")
        if (robotsMetaTag) {
            robotsMetaTag.setAttribute('content', contentValue)
        } else {
            const meta = document.createElement('meta')
            meta.setAttribute('name', 'robots')
            meta.setAttribute('content', contentValue)
            document.head.appendChild(meta)
        }
    }

    async canonicalizeLocale() {
        const { city, locale } = this.reqParamsQuery
        const path = window.location.pathname
        const possibleLocalesParts = ['fr', 'en', 'ca', 'us']

        if (possibleLocalesParts.some((e) => path.includes(e)) && !city) {
            if (this.isLocaleDuplicated()) {
                const url = this.removeDuplicatedLocale()
                const city = this.getCityFromUrl(url)
                if (city) {
                    return this.setCanonicalLink(this.getCanonicalUrl(city))
                }

                return this.setCanonicalLink(url)
            } else {
                const city = this.getCityFromUrl(path)
                if (city && !this.seoService.isLocale(city)) {
                    return this.setCanonicalLink(this.getCanonicalUrl(city))
                }

                return this.setCanonicalLink(this.getCanonicalUrl(null))
            }
        }

        if (city && !this.isCityParameterCorrect(locale, city)) {
            return this.setCanonicalLink(this.getCanonicalUrl(null))
        }

        if (city && !this.seoService.isLocale(locale)) {
            return this.setCanonicalLink(this.getCanonicalUrl(city))
        }

        if (
            locale &&
            !window.location.search.includes(locale) &&
            !city &&
            !this.seoService.isLocale(locale)
        ) {
            return this.setCanonicalLink(this.getCanonicalUrl(null))
        }

        if (window.location.pathname.includes('/dist')) {
            return this.setCanonicalLink(`/${this.getLocale()}`)
        }

        return this.setCanonicalLink(this.getSelfReferenceLink())
    }

    isCityParameterCorrect(locale: string, city: string) {
        if (!city) {
            return false
        }

        const stateKey = city.split('-').pop().toUpperCase()
        return Boolean(
            locale === 'en-us'
                ? this.seoDataService.getUsStates()[stateKey]
                : this.seoDataService.getCaProvinces()[stateKey]
        )
    }

    getInCityParameter(): string {
        const { city } = this.reqParamsQuery
        if (city) {
            return city
        }

        const queryParams = new URLSearchParams(window.location.search)
        if (queryParams.has('in-city')) {
            return queryParams.get('in-city')
        }

        const matches = window.location.href.match(/in-city=([^&]*)/)
        if (matches && Array.isArray(matches)) {
            return matches[1]
        }

        return null
    }

    isWrongFlyerName(name: string, locale: string) {
        if (name === 'item' || name === 'produit') {
            return false
        }

        return name !== this.getLocaleBasedFlyerNames(locale)
    }

    isWrongItemName(name: string, locale: string) {
        if (locale.includes('fr') && name === 'item') {
            return true
        }

        return locale.includes('en') && name === 'produit'
    }

    getLocale(): string {
        const locale = this.locationService.getLocaleFromAPI()
        if (locale === 'fr-ca' && !window.location.href.match(/\Wfr(\W|$)/)) {
            return 'en-ca'
        }

        return locale
    }

    getLocaleBasedFlyerNames = (locale?: string): string => {
        if (!locale) {
            locale = this.getLocale()
        }

        const names: { [key: string]: string } = {
            'en-ca': 'flyer',
            'en-us': 'weekly_ad',
            'fr-ca': 'circulaire',
        }

        return names[locale]
    }

    private isCanonical() {
        const city = this.getInCityParameter()
        const { locale } = this.reqParamsQuery
        if (
            locale &&
            (!this.isCityParameterCorrect(locale, city) ||
                !this.seoService.isLocale(locale))
        ) {
            return true
        }

        const itemName = window.location.pathname.split('/')[3]
        const needToCheck = [
            'flyer',
            'weekly_ad',
            'circulaire',
            'item',
            'produit',
        ].some((e) => e.includes(itemName))
        if (
            needToCheck &&
            (this.isWrongFlyerName(itemName, locale) ||
                this.isWrongItemName(itemName, locale))
        ) {
            return true
        }

        const queryParams = new URLSearchParams(location.search)
        return ['in-city', 'postal_code', 'postal_code?', 'locale'].some((e) =>
            queryParams.has(e)
        )
    }

    private isLocaleDuplicated() {
        const locale = this.seoService.locale
        return window.location.pathname.includes(`${locale}/${locale}`)
    }

    private removeDuplicatedLocale() {
        const locale = this.seoService.locale
        return window.location.pathname.replace(`${locale}/${locale}`, locale)
    }

    private getCityFromUrl(path: string): string | null {
        const matches = path.match(/\b\w*[-']\w*$\b/)
        if (matches) {
            return matches[0]
        }

        return null
    }
}
